// A node of a recording in the inspector — an event, a scene, a plate, a pass of a person: the
// stretch of the recording it stands for, a button that opens the recording there (the player
// runs that stretch) and one that copies a link doing the same, to send to a colleague.
import { Check, Film, Link2, Play } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'
import { documentPath } from '@/features/documents/document-path'
import { formatVideoTime } from '@/utils/video-file'
import type { VideoMoment } from './video-evidence'

const COPY = 'investigations.entityDetail.video'

/** Opens the recording's overlay at the stretch; null where no overlay is mounted (a link then). */
export type OpenRecordingAt = (moment: VideoMoment) => void

export function VideoEvidenceSection({
  caseId,
  moment,
  onOpen,
}: {
  caseId: string
  moment: VideoMoment
  onOpen: OpenRecordingAt | null
}) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const path = documentPath(caseId, moment.documentId, moment.from, moment.to)
  const span = t(`${COPY}.span`, {
    from: formatVideoTime(moment.from),
    to: formatVideoTime(moment.to),
  })
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`)
      setCopied(true)
      toast.success(t(`${COPY}.copied`))
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(t(`${COPY}.copyFailed`))
    }
  }
  return (
    <section
      className="flex flex-col gap-2 rounded-md border border-border bg-card/40 p-2.5"
      data-testid="video-evidence"
    >
      <div className="flex items-center gap-2 text-xs text-foreground">
        <Film aria-hidden className="size-3.5 text-primary" />
        <span className="font-medium">{t(`${COPY}.title`)}</span>
        <span className="tabular-nums text-muted-foreground">{span}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {onOpen ? (
          <Button type="button" variant="outline" size="xs" onClick={() => onOpen(moment)}>
            <Play aria-hidden />
            {t(`${COPY}.open`)}
          </Button>
        ) : (
          <Button variant="outline" size="xs" asChild>
            <Link to={path}>
              <Play aria-hidden />
              {t(`${COPY}.open`)}
            </Link>
          </Button>
        )}
        <Button type="button" variant="ghost" size="xs" onClick={() => void copy()}>
          {copied ? <Check aria-hidden /> : <Link2 aria-hidden />}
          {t(`${COPY}.copy`)}
        </Button>
      </div>
    </section>
  )
}
